import { useState } from 'react';
import { AssignmentStatus, ComplianceSnapshot, ComplianceSummary } from '../../apis/api-client';
import { Box, Chip, MenuItem, Select, Stack, Tooltip, Typography } from '../ui';
import { statusColor, statusLabel } from '../../utils/reportingHelpers';

interface Props {
  summary: ComplianceSummary | null;
  snapshots: ComplianceSnapshot[];
  days: number;
  onChangeDays: (days: number) => void;
}

const RANGE_OPTIONS = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m',  days: 180 },
  { label: '1y',  days: 365 },
];

const SPARK_WIDTH = 180;
const SPARK_HEIGHT = 44;

function scoreColor(p: number): string {
  if (p >= 95) return 'success.main';
  if (p >= 80) return 'warning.main';
  return 'error.main';
}

export default function ComplianceSummaryStrip({ summary, snapshots, days, onChangeDays }: Props) {
  const [localDays, setLocalDays] = useState(days);

  const total = summary?.total ?? 0;
  const completed = summary?.completed ?? 0;
  const completionPct = total === 0 ? 0 : Math.round((completed / total) * 100);

  const sparkPoints = snapshots.map(s => s.total === 0 ? 0 : (s.completed / s.total) * 100);
  const sparkPath = (() => {
    if (sparkPoints.length === 0) return '';
    const xFor = (i: number) => sparkPoints.length <= 1 ? 0 : (i / (sparkPoints.length - 1)) * SPARK_WIDTH;
    const yFor = (p: number) => SPARK_HEIGHT - 2 - (p / 100) * (SPARK_HEIGHT - 4);
    return sparkPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(p).toFixed(1)}`).join(' ');
  })();

  if (total === 0) {
    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 3, py: 1.5, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">No assignment data for the current filters.</Typography>
      </Box>
    );
  }

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={3}
      alignItems={{ md: 'center' }}
      sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 1,
        px: 3, py: 2, mb: 3, bgcolor: 'grey.50',
      }}
    >
      <Box sx={{ minWidth: 110 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color: scoreColor(completionPct), lineHeight: 1 }}>
          {completionPct}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {completed} of {total} complete
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flexGrow: 1, rowGap: 1 }}>
        {[
          { label: AssignmentStatus.Overdue,   count: summary?.overdue ?? 0 },
          { label: AssignmentStatus.Active,    count: summary?.active ?? 0 },
          { label: AssignmentStatus.Upcoming,  count: summary?.upcoming ?? 0 },
          { label: AssignmentStatus.Completed, count: summary?.completed ?? 0 },
        ].map(({ label, count }) => (
          <Chip
            key={label}
            label={`${count} ${statusLabel(label)}`}
            size="small"
            sx={{ bgcolor: statusColor(label), fontWeight: 600 }}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title={`Completion % over the last ${localDays} days (workspace-wide, ignores filters)`}>
          <Box sx={{ width: SPARK_WIDTH, height: SPARK_HEIGHT }}>
            <svg viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`} width="100%" height="100%" style={{ display: 'block' }}>
              {sparkPath && (
                <>
                  <path d={`${sparkPath} L ${SPARK_WIDTH} ${SPARK_HEIGHT} L 0 ${SPARK_HEIGHT} Z`} fill="rgba(25, 118, 210, 0.1)" />
                  <path d={sparkPath} fill="none" stroke="#1976d2" strokeWidth="1.5" />
                </>
              )}
            </svg>
          </Box>
        </Tooltip>
        <Select
          value={localDays}
          onChange={e => {
            const v = Number(e.target.value);
            setLocalDays(v);
            onChangeDays(v);
          }}
          size="small"
          variant="standard"
          disableUnderline
          sx={{ '& .MuiSelect-select': { py: 0, fontSize: '0.75rem' } }}
        >
          {RANGE_OPTIONS.map(o => (
            <MenuItem key={o.days} value={o.days}>{o.label}</MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>
  );
}
